import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { CheckCircle, XCircle, Mail, Loader2, ArrowLeft, RefreshCw } from 'lucide-react';

const EmailVerificationPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('verifying'); // verifying, success, error, expired
  const [message, setMessage] = useState('');
  const [email, setEmail] = useState('');
  const [resending, setResending] = useState(false);
  const [resendEmail, setResendEmail] = useState('');
  
  const token = searchParams.get('token');
  const userType = searchParams.get('type') || 'seller';

  useEffect(() => {
    if (token) {
      verifyEmail();
    } else {
      setStatus('error');
      setMessage('Link di verifica non valido');
    }
  }, [token]);

  const verifyEmail = async () => {
    try {
      const response = await axios.post('/auth/verify-email', {
        token,
        user_type: userType
      });
      
      setStatus('success');
      setMessage(response.data.message);
      setEmail(response.data.email);
      
      toast.success('Email verificata con successo!');
      
      // Redirect to login after 3 seconds
      setTimeout(() => {
        navigate('/login');
      }, 3000);
      
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Errore durante la verifica';
      
      if (errorMsg.includes('scaduto')) {
        setStatus('expired');
      } else {
        setStatus('error');
      }
      
      setMessage(errorMsg);
    }
  };

  const handleResendVerification = async () => {
    if (!resendEmail) {
      toast.error('Inserisci la tua email');
      return;
    }
    
    setResending(true);
    
    try {
      await axios.post('/auth/resend-verification', {
        email: resendEmail,
        user_type: userType
      });
      
      toast.success('Nuovo link di verifica inviato! Controlla la tua email.');
      setResendEmail('');
      
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nell\'invio');
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#F9F9F7] to-white flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        {/* Header */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center space-x-2 mb-6">
            <img 
              src="/weqasa-logo.png" 
              alt="WEQASA" 
              className="h-12 w-auto object-contain"
            />
          </Link>
        </div>

        {/* Verification Card */}
        <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
          {/* Status Icon */}
          <div className={`p-8 text-center ${
            status === 'verifying' ? 'bg-blue-50' :
            status === 'success' ? 'bg-green-50' :
            status === 'expired' ? 'bg-amber-50' :
            'bg-red-50'
          }`}>
            {status === 'verifying' && (
              <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto">
                <Loader2 className="h-10 w-10 text-blue-600 animate-spin" />
              </div>
            )}
            {status === 'success' && (
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto">
                <CheckCircle className="h-10 w-10 text-green-600" />
              </div>
            )}
            {status === 'expired' && (
              <div className="w-20 h-20 bg-amber-100 rounded-full flex items-center justify-center mx-auto">
                <RefreshCw className="h-10 w-10 text-amber-600" />
              </div>
            )}
            {status === 'error' && (
              <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto">
                <XCircle className="h-10 w-10 text-red-600" />
              </div>
            )}
          </div>

          {/* Content */}
          <div className="p-8">
            <h1 className={`text-2xl font-bold text-center mb-2 ${
              status === 'verifying' ? 'text-blue-900' :
              status === 'success' ? 'text-green-900' :
              status === 'expired' ? 'text-amber-900' :
              'text-red-900'
            }`}>
              {status === 'verifying' && 'Verifica in corso...'}
              {status === 'success' && 'Email Verificata!'}
              {status === 'expired' && 'Link Scaduto'}
              {status === 'error' && 'Verifica Fallita'}
            </h1>

            <p className="text-gray-600 text-center mb-6">
              {status === 'verifying' && 'Stiamo verificando il tuo indirizzo email...'}
              {status === 'success' && (
                <>
                  Il tuo account è stato attivato con successo.
                  {email && <span className="block text-sm mt-1">({email})</span>}
                </>
              )}
              {status === 'expired' && 'Il link di verifica è scaduto. Richiedi un nuovo link.'}
              {status === 'error' && message}
            </p>

            {/* Success Actions */}
            {status === 'success' && (
              <div className="space-y-3">
                <p className="text-sm text-gray-500 text-center">
                  Verrai reindirizzato al login tra pochi secondi...
                </p>
                <Button
                  onClick={() => navigate('/login')}
                  className="w-full bg-[#0071e3] hover:bg-[#0A3844]"
                >
                  Vai al Login
                </Button>
              </div>
            )}

            {/* Expired/Error - Resend Form */}
            {(status === 'expired' || status === 'error') && (
              <div className="space-y-4">
                <div className="border-t pt-4">
                  <p className="text-sm text-gray-600 mb-3 text-center">
                    Inserisci la tua email per ricevere un nuovo link di verifica:
                  </p>
                  <div className="space-y-3">
                    <Input
                      type="email"
                      placeholder="La tua email"
                      value={resendEmail}
                      onChange={(e) => setResendEmail(e.target.value)}
                    />
                    <Button
                      onClick={handleResendVerification}
                      disabled={resending}
                      className="w-full bg-[#0071e3] hover:bg-[#005BB5]"
                    >
                      {resending ? (
                        <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Invio in corso...</>
                      ) : (
                        <><Mail className="h-4 w-4 mr-2" /> Invia Nuovo Link</>
                      )}
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Back to Home */}
        <div className="text-center mt-6">
          <Link 
            to="/" 
            className="inline-flex items-center text-[#0071e3] hover:text-[#0071e3] transition-colors"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Torna alla Homepage
          </Link>
        </div>
      </div>
    </div>
  );
};

export default EmailVerificationPage;
